import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api';
import type { AllowanceTypeFormData } from '../types';

const KEYS = {
  all: ['allowance-types'] as const,
  list: (params?: Record<string, unknown>) => [...KEYS.all, 'list', params] as const,
  active: () => [...KEYS.all, 'active'] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

export function useAllowanceTypes(params?: { page?: number; limit?: number; search?: string; isActive?: string }) {
  return useQuery({
    queryKey: KEYS.list(params as Record<string, unknown>),
    queryFn: async () => {
      const { data } = await adminApi.getAllowanceTypes(params);
      return data;
    },
  });
}

export function useActiveAllowanceTypes() {
  return useQuery({
    queryKey: KEYS.active(),
    queryFn: async () => {
      const { data } = await adminApi.getActiveAllowanceTypes();
      return data.allowanceTypes;
    },
  });
}

export function useAllowanceTypeDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await adminApi.getAllowanceTypeById(id!);
      return data.allowanceType;
    },
    enabled: !!id,
  });
}

export function useCreateAllowanceType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AllowanceTypeFormData) => {
      const { data: result } = await adminApi.createAllowanceType(data);
      return result.allowanceType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateAllowanceType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AllowanceTypeFormData> }) => {
      const { data: result } = await adminApi.updateAllowanceType(id, data);
      return result.allowanceType;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteAllowanceType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await adminApi.deleteAllowanceType(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}
